/**
 * CampaignCard — one cluster of correlated risk events. The card
 * leads with severity, a short factual summary, the affected
 * accounts, the strongest scoring reasons, and the suggested next
 * investigation step. WAF jargon (ASN, JA4, p95, stored endpoint group)
 * sits in a collapsed "Technical details" section.
 */
import { useState } from "react";
import { Badge, Button, Card } from "@/components/primitives";
import { MonoValue } from "@/components/primitives/MonoValue";
import { useClipboard } from "@/hooks/useClipboard";
import { useUrlState } from "@/hooks/useUrlState";
import { pivotTo } from "@/lib/pivot";
import { fmtNumber } from "@/lib/format";
import { formatTimestamp } from "@/lib/time";
import { countryName } from "@/config/geo";
import { endpointAreaLabel } from "@/config/filters";
import { ReasonChips } from "@/components/reasons/ReasonChips";
import { parseReasons } from "@/lib/reasonCodes";
import type { CampaignV1 } from "@/types/v1";

interface Props {
  campaign: CampaignV1;
  onOpenInvestigations: (preset: Record<string, string>) => void;
}

/** Map stored endpoint-group keys to vocabulary support staff use. */
const ENDPOINT_PHRASES: Record<string, { gerund: string; noun: string }> = {
  auth:          { gerund: "login attempts",              noun: "login"        },
  login:         { gerund: "login attempts",              noun: "login"        },
  sms:           { gerund: "message requests",            noun: "message"      },
  register:      { gerund: "registration attempts",       noun: "registration" },
  signup:        { gerund: "registration attempts",       noun: "registration" },
  payments:      { gerund: "payment attempts",            noun: "payment"      },
  payment:       { gerund: "payment attempts",            noun: "payment"      },
  messaging:     { gerund: "message requests",            noun: "message"      },
  bookings:      { gerund: "booking requests",            noun: "booking"      },
  gift_vouchers: { gerund: "gift voucher requests",       noun: "gift voucher" },
  other:         { gerund: "requests",                    noun: "request"      },
};
function endpointWords(key: string) {
  return ENDPOINT_PHRASES[key.toLowerCase()] ?? { gerund: `${key} requests`, noun: key };
}

/** Severity → plain label, badge tone, and an English adjective. */
function severityCopy(s: CampaignV1["severity"]) {
  if (s === "high")
    return { label: "Action recommended", tone: "high"        as const, adj: "very suspicious" };
  if (s === "suspicious")
    return { label: "Worth a look",       tone: "suspicious"  as const, adj: "suspicious"      };
  return   { label: "Just monitoring",    tone: "watch"       as const, adj: "unusual"         };
}

/** Confidence → plain label. */
function confidenceCopy(c: CampaignV1["confidence"]) {
  if (c === "high")   return "We're confident";
  if (c === "medium") return "Fairly confident";
  return "Early signal";
}

export function CampaignCard({ campaign: c, onOpenInvestigations }: Props) {
  const { copy, copied } = useClipboard();
  const [, patchUrl] = useUrlState();
  const [showTech, setShowTech] = useState(false);
  const summary = buildSummary(c);

  const sev = severityCopy(c.severity);
  const conf = confidenceCopy(c.confidence);
  const ep = endpointWords(c.topEndpointGroup);

  // Short, factual one-line summary. We deliberately avoid "ASN",
  // "JA4", "p95" etc. so a non-technical reader gets the gist quickly.
  const headline = buildHeadline(c, ep.gerund);
  const summarySentence = buildSummarySentence(c, ep, sev.adj);
  const parsedReasons = parseReasons(c.reasons.join(";"));

  return (
    <Card className="flex flex-col gap-4">
      <header className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <Badge tone={sev.tone}>{sev.label}</Badge>
            <span className="text-[11px] text-fg-subtle">{conf}</span>
          </div>
          <h3 className="mt-1.5 text-base font-semibold text-fg">{headline}</h3>
          <p className="mt-1 text-sm text-fg-muted">{summarySentence}</p>
        </div>
      </header>

      <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
        <PlainStat
          label={`${cap(ep.noun)} attempts`}
          value={fmtNumber(c.events, { compact: true })}
          hint="In this time window"
        />
        <PlainStat
          label="Clearly suspicious"
          value={fmtNumber(c.highRisk, { compact: true })}
          tone="high"
          hint={c.events ? `${Math.round((c.highRisk / c.events) * 100)}% of the attempts` : undefined}
        />
        <PlainStat
          label="Different sources"
          value={fmtNumber(c.distinctIps, { compact: true })}
          hint="Devices or networks involved"
        />
      </div>

      <p className="rounded border border-accent/40 bg-accent/5 px-3 py-2 text-xs text-fg">
        <span className="font-semibold">What to do next:</span> {c.nextStep}
      </p>

      {parsedReasons.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-[11px] text-fg-subtle">Top reasons:</span>
          <ReasonChips parsed={parsedReasons} top={3} />
        </div>
      )}

      {(c.topEmails?.length || c.topBusinesses?.length) ? (
        <div className="rounded border border-border bg-surface-2 px-3 py-2.5">
          <div className="mb-1.5 text-xs font-medium text-fg">Affected accounts</div>
          <div className="flex flex-col gap-1.5 text-xs">
            {c.topEmails?.length ? (
              <div className="flex flex-wrap items-center gap-1.5">
                <span className="text-fg-subtle">Emails:</span>
                {c.topEmails.map((e) => (
                  <button
                    key={e}
                    type="button"
                    title={`Look up ${e}`}
                    onClick={() => pivotTo(patchUrl, { email: e }, "investigations")}
                    className="rounded border border-border bg-surface-1 px-2 py-0.5 font-mono text-[11px] text-fg hover:border-accent hover:text-accent"
                  >{e}</button>
                ))}
                {c.distinctEmails > c.topEmails.length && (
                  <span className="text-[11px] text-fg-subtle">and {fmtNumber(c.distinctEmails - c.topEmails.length, { compact: true })} more</span>
                )}
              </div>
            ) : null}
            {c.topBusinesses?.length ? (
              <div className="flex flex-wrap items-center gap-1.5">
                <span className="text-fg-subtle">Businesses:</span>
                {c.topBusinesses.map((b) => (
                  <button
                    key={b}
                    type="button"
                    title={`Look up ${b}`}
                    onClick={() => pivotTo(patchUrl, { businessId: b }, "investigations")}
                    className="rounded border border-border bg-surface-1 px-2 py-0.5 font-mono text-[11px] text-fg hover:border-accent hover:text-accent"
                  >{b}</button>
                ))}
                {c.distinctBusinesses > c.topBusinesses.length && (
                  <span className="text-[11px] text-fg-subtle">and {fmtNumber(c.distinctBusinesses - c.topBusinesses.length, { compact: true })} more</span>
                )}
              </div>
            ) : null}
            {!c.topEmails?.length && !c.topBusinesses?.length && (
              <span className="inline-flex rounded-md border border-border bg-surface-3 px-1.5 py-0.5 text-[10px] font-medium text-fg-subtle">unknown</span>
            )}
          </div>
        </div>
      ) : null}

      <div className="border-t border-border pt-2">
        <button
          type="button"
          onClick={() => setShowTech((s) => !s)}
          className="flex items-center gap-1 text-[11px] text-fg-subtle hover:text-fg"
          aria-expanded={showTech}
        >
          <span className="inline-block w-3">{showTech ? "▾" : "▸"}</span>
          Technical details {showTech ? "" : "(for engineers)"}
        </button>
        {showTech && (
          <div className="mt-2 grid grid-cols-1 gap-x-6 gap-y-1 rounded border border-border bg-surface-2 px-3 py-2 text-[11px] text-fg-muted sm:grid-cols-2">
            <KV k="First seen" v={formatTimestamp(c.firstSeen)} />
            <KV k="Last seen"  v={formatTimestamp(c.lastSeen)} />
            <KV k="Area" v={endpointAreaLabel(c.topEndpointGroup)} />
            <KV k="Stored group" v={c.topEndpointGroup} />
            {c.topAsn != null && <KV k="ASN" v={<MonoValue value={`AS${c.topAsn}`} />} />}
            {c.topJa4 && <KV k="JA4 fingerprint" v={<MonoValue value={c.topJa4} truncate />} />}
            {c.countries.length > 0 && (
              <KV
                k="Countries"
                v={c.countries.slice(0, 6).map(countryName).join(", ") + (c.countries.length > 6 ? "…" : "")}
              />
            )}
            <KV k="Median risk score" v={String(Math.round(c.medianScore))} />
            <KV k="p95 risk score"    v={String(Math.round(c.p95Score))} />
            <KV k="Distinct sessions" v={fmtNumber(c.distinctSessions, { compact: true })} />
            <KV k="Distinct customers" v={fmtNumber(c.distinctEmails, { compact: true })} />
          </div>
        )}
      </div>

      <footer className="flex flex-wrap items-center gap-2">
        <Button size="sm" variant="primary" onClick={() => onOpenInvestigations(c.filterPreset)}>
          See all events
        </Button>
        <Button size="sm" variant="ghost" onClick={() => void copy(summary)}>
          {copied ? "Copied" : "Copy summary"}
        </Button>
      </footer>
    </Card>
  );
}

function PlainStat({ label, value, hint, tone }: {
  label: string; value: string; hint?: string; tone?: "high";
}) {
  return (
    <div className="rounded border border-border bg-surface-2 px-3 py-2">
      <div className="text-[11px] text-fg-subtle">{label}</div>
      <div className={"mt-0.5 font-mono text-xl font-semibold " + (tone === "high" ? "text-sev-high" : "text-fg")}>{value}</div>
      {hint && <div className="mt-0.5 text-[11px] text-fg-subtle">{hint}</div>}
    </div>
  );
}

function KV({ k, v }: { k: string; v: React.ReactNode }) {
  return (
    <div className="flex items-baseline gap-2">
      <span className="text-fg-subtle">{k}:</span>
      <span className="text-fg">{v}</span>
    </div>
  );
}

function cap(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/** Produce a concise headline like:
 *  "Lots of suspicious sign-in attempts on your customers" */
function buildHeadline(c: CampaignV1, gerund: string): string {
  const volume = c.events >= 1000 ? "Lots of"
    : c.events >= 200 ? "A burst of"
    : "A small spike in";
  const adj = c.severity === "high" ? "highly suspicious"
    : c.severity === "suspicious" ? "suspicious"
    : "unusual";
  return `${volume} ${adj} ${gerund} on your customers`;
}

/** One-line factual description of what's happening. */
function buildSummarySentence(
  c: CampaignV1,
  ep: { gerund: string; noun: string },
  adj: string,
): string {
  const events = c.events.toLocaleString();
  const ips = c.distinctIps.toLocaleString();
  const country = c.countries[0] ? countryName(c.countries[0]) : null;
  const more = c.countries.length > 1 ? ` and ${c.countries.length - 1} other ${c.countries.length === 2 ? "country" : "countries"}` : "";
  const where = country ? `, mostly from ${country}${more}` : "";
  const customers = c.distinctEmails > 0
    ? `, affecting around ${fmtNumber(c.distinctEmails, { compact: true })} of your customers.`
    : ".";
  return `We saw about ${events} ${adj} ${ep.gerund} from ${ips} different sources${where}${customers}`;
}

function buildSummary(c: CampaignV1): string {
  const ep = endpointWords(c.topEndpointGroup);
  const sev = severityCopy(c.severity);
  return [
    `[${sev.label}] ${buildHeadline(c, ep.gerund)}`,
    `When: ${formatTimestamp(c.firstSeen)} → ${formatTimestamp(c.lastSeen)}`,
    `What: ${buildSummarySentence(c, ep, sev.adj)}`,
    `Numbers: ${c.events.toLocaleString()} ${ep.noun} attempts, ${c.highRisk.toLocaleString()} clearly suspicious, ${c.distinctIps.toLocaleString()} different sources, ${c.distinctEmails.toLocaleString()} customers touched.`,
    c.topEmails?.length ? `Top affected emails: ${c.topEmails.join(", ")}` : "",
    c.topBusinesses?.length ? `Top affected businesses: ${c.topBusinesses.join(", ")}` : "",
    `What to do next: ${c.nextStep}`,
    `Technical: area=${endpointAreaLabel(c.topEndpointGroup)}, stored group=${c.topEndpointGroup}${c.topAsn ? `, ASN=AS${c.topAsn}` : ""}${c.topJa4 ? `, JA4=${c.topJa4}` : ""}, p95 score ${Math.round(c.p95Score)}.`,
    "Note: this describes scored traffic patterns, not confirmed account compromise.",
  ].filter(Boolean).join("\n");
}
